import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { format } from "date-fns"
import { cn } from "../../lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Calendar } from "./calendar"

/**
 * Unified DatePicker component combining Calendar + Popover.
 *
 * Props:
 *  - value: string (YYYY-MM-DD for date mode, YYYY-MM-DDTHH:mm for datetime mode) or Date or null
 *  - onChange: (value: string) => void  — always returns a string in the same format as input
 *  - mode: 'date' | 'datetime' (default: 'date')
 *  - min: string | Date (minimum selectable date)
 *  - max: string | Date (maximum selectable date)
 *  - id: string (for label association)
 *  - name: string (for form compatibility)
 *  - error: boolean (shows red border)
 *  - placeholder: string
 *  - className: string
 */
const DatePicker = React.forwardRef(({
  value,
  onChange,
  mode = "date",
  min,
  max,
  id,
  name,
  error = false,
  placeholder,
  className,
}, ref) => {
  const dateFormat = mode === "datetime" ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd"
  const displayFormat = mode === "datetime" ? "MMM dd, yyyy h:mm a" : "MMM dd, yyyy"

  // Convert string value to Date for the Calendar
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined
    if (value instanceof Date) return value
    const d = new Date(value)
    return isNaN(d.getTime()) ? undefined : d
  }, [value])

  // Convert min/max to Date
  const minDate = React.useMemo(() => {
    if (!min) return undefined
    if (min instanceof Date) return min
    const d = new Date(min)
    return isNaN(d.getTime()) ? undefined : d
  }, [min])

  const maxDate = React.useMemo(() => {
    if (!max) return undefined
    if (max instanceof Date) return max
    const d = new Date(max)
    return isNaN(d.getTime()) ? undefined : d
  }, [max])

  const handleSelect = (day) => {
    if (!day) {
      onChange("")
      return
    }

    if (mode === "datetime") {
      // Preserve existing time if we have a current value, otherwise default to now
      const existingDate = selectedDate
      if (existingDate) {
        day.setHours(existingDate.getHours(), existingDate.getMinutes(), 0, 0)
      } else {
        const now = new Date()
        day.setHours(now.getHours(), now.getMinutes(), 0, 0)
      }
    }

    onChange(format(day, dateFormat))
  }

  const handleTimeChange = (e) => {
    const timeValue = e.target.value // "HH:mm"
    if (!timeValue) return

    const baseDate = selectedDate || new Date()
    const [hours, minutes] = timeValue.split(":").map(Number)
    baseDate.setHours(hours, minutes, 0, 0)
    onChange(format(baseDate, dateFormat))
  }

  const timeValue = React.useMemo(() => {
    if (!selectedDate) return ""
    const h = String(selectedDate.getHours()).padStart(2, "0")
    const m = String(selectedDate.getMinutes()).padStart(2, "0")
    return `${h}:${m}`
  }, [selectedDate])

  return (
    <div className="relative" ref={ref}>
      {/* Hidden input for form compatibility */}
      {name && (
        <input type="hidden" name={name} value={value || ""} />
      )}

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            className={cn(
              "flex h-9 w-full items-center justify-start rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-left",
              error && "border-destructive focus:ring-destructive",
              !value && "text-muted-foreground",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            {value ? format(selectedDate, displayFormat) : placeholder || (mode === "datetime" ? "Pick date and time" : "Pick a date")}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            disabled={[
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
            initialFocus
          />
          {mode === "datetime" && (
            <div className="border-t border-border p-3">
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Time</label>
              <input
                type="time"
                value={timeValue}
                onChange={handleTimeChange}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
})
DatePicker.displayName = "DatePicker"

export { DatePicker }
